export interface ParsedValue<T> {
  value: T | null;
  error: string | null;
}

export interface ObjectDiffResult {
  added: Record<string, unknown>;
  removed: Record<string, unknown>;
  changed: Record<string, { from: unknown; to: unknown }>;
}

export function parseArrayInput(input: string): ParsedValue<unknown[]> {
  const trimmed = input.trim();

  if (!trimmed) {
    return { value: [], error: null };
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return { value: parsed, error: null };
    }

    return {
      value: null,
      error: 'Input must be a JSON array, comma-separated list, or line-separated list.',
    };
  } catch {
    const hasLines = /\r?\n/.test(trimmed);
    const parts = hasLines ? trimmed.split(/\r?\n/) : trimmed.split(',');
    return {
      value: parts.map((part) => coerceListValue(part.trim())).filter((part) => part !== ''),
      error: null,
    };
  }
}

export function parseObjectInput(input: string): ParsedValue<Record<string, unknown>> {
  const trimmed = input.trim();

  if (!trimmed) {
    return { value: {}, error: null };
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (isPlainObject(parsed)) {
      return { value: parsed as Record<string, unknown>, error: null };
    }

    return { value: null, error: 'Input must be a JSON object.' };
  } catch (error) {
    return {
      value: null,
      error: error instanceof Error ? error.message : 'Invalid JSON object.',
    };
  }
}

export function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

export function minifyJson(value: unknown): string {
  return JSON.stringify(value);
}

export function stableValueKey(value: unknown): string {
  return stableStringify(value);
}

export function uniqueArray(values: unknown[]): unknown[] {
  const seen = new Set<string>();

  return values.filter((value) => {
    const key = stableValueKey(value);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

export function sortArray(values: unknown[]): unknown[] {
  return [...values].sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true }),
  );
}

export function flattenArray(values: unknown[]): unknown[] {
  return values.flat(Number.POSITIVE_INFINITY);
}

export function chunkArray(values: unknown[], size: number): unknown[][] {
  const safeSize = Math.max(1, Math.floor(size));
  const chunks: unknown[][] = [];

  for (let index = 0; index < values.length; index += safeSize) {
    chunks.push(values.slice(index, index + safeSize));
  }

  return chunks;
}

export function arrayDifference(left: unknown[], right: unknown[]): unknown[] {
  const rightKeys = new Set(right.map(stableValueKey));
  return left.filter((value) => !rightKeys.has(stableValueKey(value)));
}

export function arrayIntersection(left: unknown[], right: unknown[]): unknown[] {
  const rightKeys = new Set(right.map(stableValueKey));
  return uniqueArray(left.filter((value) => rightKeys.has(stableValueKey(value))));
}

export function arrayToCsv(values: unknown[]): string {
  if (values.length === 0) {
    return '';
  }

  if (values.every(isPlainObject)) {
    const rows = values as Record<string, unknown>[];
    const headers = uniqueArray(rows.flatMap((row) => Object.keys(row))) as string[];
    return [
      headers.map(csvCell).join(','),
      ...rows.map((row) => headers.map((header) => csvCell(row[header])).join(',')),
    ].join('\n');
  }

  return values.map(csvCell).join('\n');
}

export function sortObjectKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortObjectKeys);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return Object.keys(value)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = sortObjectKeys((value as Record<string, unknown>)[key]);
      return acc;
    }, {});
}

export function flattenObject(
  value: Record<string, unknown>,
  prefix = '',
): Record<string, unknown> {
  return Object.entries(value).reduce<Record<string, unknown>>((acc, [key, item]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;

    if (isPlainObject(item) && Object.keys(item).length > 0) {
      Object.assign(acc, flattenObject(item as Record<string, unknown>, nextKey));
    } else {
      acc[nextKey] = item;
    }

    return acc;
  }, {});
}

export function objectToQueryString(value: Record<string, unknown>): string {
  return Object.entries(flattenObject(value))
    .filter(([, item]) => item !== undefined && item !== null)
    .map(([key, item]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`)
    .join('&');
}

export function mergeObjects(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
): Record<string, unknown> {
  return deepMerge(left, right);
}

export function diffObjects(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
): ObjectDiffResult {
  const leftFlat = flattenObject(left);
  const rightFlat = flattenObject(right);
  const keys = uniqueArray([...Object.keys(leftFlat), ...Object.keys(rightFlat)]) as string[];

  return keys.reduce<ObjectDiffResult>(
    (acc, key) => {
      const leftHas = Object.prototype.hasOwnProperty.call(leftFlat, key);
      const rightHas = Object.prototype.hasOwnProperty.call(rightFlat, key);

      if (!leftHas && rightHas) {
        acc.added[key] = rightFlat[key];
      } else if (leftHas && !rightHas) {
        acc.removed[key] = leftFlat[key];
      } else if (stableValueKey(leftFlat[key]) !== stableValueKey(rightFlat[key])) {
        acc.changed[key] = { from: leftFlat[key], to: rightFlat[key] };
      }

      return acc;
    },
    { added: {}, removed: {}, changed: {} },
  );
}

function coerceListValue(value: string): unknown {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === 'null') return null;
  if (value !== '' && !Number.isNaN(Number(value))) return Number(value);
  return value;
}

function csvCell(value: unknown): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  const safeText = text ?? '';

  if (/[",\n\r]/.test(safeText)) {
    return `"${safeText.replace(/"/g, '""')}"`;
  }

  return safeText;
}

function deepMerge(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...left };

  for (const [key, value] of Object.entries(right)) {
    const existing = result[key];
    result[key] =
      isPlainObject(existing) && isPlainObject(value)
        ? deepMerge(existing as Record<string, unknown>, value as Record<string, unknown>)
        : value;
  }

  return result;
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (isPlainObject(value)) {
    return `{${Object.keys(value)
      .sort()
      .map(
        (key) =>
          `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`,
      )
      .join(',')}}`;
  }

  return JSON.stringify(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
